import { ReactElement, useMemo } from "react";
import { 
  Autocomplete, AutocompleteProps, AutocompleteRenderInputParams, Box, Popper, PopperProps, styled, SxProps, Tooltip 
} from '@mui/material';
import { CabComponentProps } from "../cabStyled";
import colors from "../../../colors";
import { CabToggleChip } from "../CabToggleChip";
import { CabTooltip } from "../CabTooltip";
import { CabIcon } from "../CabIcon";
import { CabInput } from "../CabInput";
import { IoChevronDown, IoCloseOutline } from "react-icons/io5";


export interface GenericAutocompleteOption<T, M> { 
  value: T, 
  label: string, 
  color?: string, 
  key?: number, 
  meta?: M
  tooltipTitle?: string
  disabled?: boolean
}

export type GenericAutocompleteProps<T, M = {[key: string]: string}> = 
AutocompleteProps<(GenericAutocompleteOption<T, M> & { 
  disabled?: boolean;
  tooltipTitle?: string;
  disabledChip?: boolean
  icon?: React.ReactElement
}), boolean, boolean, boolean>;

type AutocompleteOption<T, M> = GenericAutocompleteProps<T, M>['options'][number];
type LegacyRenderTags<T, M> = (
  value: AutocompleteOption<T, M>[],
  getTagProps: (args: { index: number }) => Record<string, unknown>,
  ownerState?: unknown
) => React.ReactNode;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GenericAutocomplete = <T, M>(props: GenericAutocompleteProps<T, M>) => (
  <Autocomplete {...props} />
);

export interface CabAutocompleteProps<T, M> extends CabComponentProps<GenericAutocompleteProps<T, M>> {
  options: GenericAutocompleteProps<T, M>['options'];
  groupBy?: GenericAutocompleteProps<T, M>['groupBy'];
  renderGroup?: GenericAutocompleteProps<T, M>['renderGroup'];
  getOptionLabel?: GenericAutocompleteProps<T, M>['getOptionLabel'];
  filterOptions?: GenericAutocompleteProps<T, M>['filterOptions'];
  onInputChange?: (value: string) => void
  onChange: (value: T|T[]|null) => void
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  renderTags?: LegacyRenderTags<T, M>;
  renderValue?: GenericAutocompleteProps<T, M>['renderValue'];
  renderOption?: GenericAutocompleteProps<T, M>['renderOption'];
  alignRight?: boolean;
  size?: GenericAutocompleteProps<T, M>['size'];
  disabled?: GenericAutocompleteProps<T, M>['disabled'];
  readOnly?: GenericAutocompleteProps<T, M>['readOnly'];
  label?: string;
  ListboxProps?: NonNullable<GenericAutocompleteProps<T, M>['slotProps']>['listbox'];
  value?: T|T[]|null;
  inputValue?: GenericAutocompleteProps<T, M>['inputValue'];
  multiple?: GenericAutocompleteProps<T, M>['multiple'];
  openOnFocus?: GenericAutocompleteProps<T, M>['openOnFocus'];
  onFocus?: GenericAutocompleteProps<T, M>['onFocus']
  onBlur?: GenericAutocompleteProps<T, M>['onBlur']
  onKeyDown?: GenericAutocompleteProps<T, M>['onKeyDown']
  defaultValue?: T|T[];
  placeholder?: string;
  className?: GenericAutocompleteProps<T, M>['className'];
  clearable?: boolean;
  hideArrow?: boolean;
  inputRef?:  React.Ref<ReactElement>;
  noOptionsText?: GenericAutocompleteProps<T, M>['noOptionsText'];
  autoSelect?: GenericAutocompleteProps<T, M>['autoSelect'];
  autoHighlight?: GenericAutocompleteProps<T, M>['autoHighlight'];
  freeSolo?: GenericAutocompleteProps<T, M>['freeSolo'];
  getOptionsDisabled?: GenericAutocompleteProps<T, M>['getOptionDisabled'];
  blurOnSelect?: GenericAutocompleteProps<T, M>['blurOnSelect'];
  narrow?: boolean;
  loading?: boolean;
  helperText?: string;
  id?: string;
  optionIconPlacement?: 'start' | 'end';
  popperSx?: SxProps;
}

export const CabAutocomplete = <T, M>({
  onChange, onInputChange, options, value, inputValue, multiple, defaultValue, placeholder, clearable, loading, id,
  label, ListboxProps, getOptionLabel, hideArrow, noOptionsText, narrow, openOnFocus = true, disabled, readOnly, size,
  autoSelect, autoHighlight, freeSolo, sx, className, overrides, filterOptions, onFocus, onBlur, onKeyDown, inputRef,
  helperText, groupBy, renderGroup, getOptionsDisabled, alignRight, blurOnSelect, optionIconPlacement, renderTags,
  renderValue, renderInput, renderOption, popperSx
}: CabAutocompleteProps<T, M>): ReactElement|null => {

  const mapValue = (val: T |T[] | null | undefined) => {
    if (val == null) return undefined;
    if (Array.isArray(val)) {
      const mappedVal = val.map(v => {
        const foundOption = options.find(o => o.value === v);
        return {
          value: v,
          label: foundOption?.label || '', 
          color: foundOption?.color || '',
          disabled: foundOption?.disabled,
          disabledChip: foundOption?.disabledChip,
          tooltipTitle: foundOption?.tooltipTitle,
          icon: foundOption?.icon,
        };
      });
      return mappedVal;
    } else {
      const mappedVal = {
        value: val,
        label: options.find(o => o.value === val)?.label || '',
      };
      return mappedVal;
    };
  };
  
  const hasOptions = options.length === 0;
  const valueJson = JSON.stringify(value);
  const defaultValueJson = JSON.stringify(defaultValue);
  // It's possible on any given render for `value` and/or `options` to change, hence the memoizations below.
  /* eslint-disable react-hooks/exhaustive-deps */
  // `options` can be huge, so only map when the input value actually changed, or options got populated (>0 options)
  const mappedValue = useMemo(() => mapValue(value), [valueJson, hasOptions]);
  const mappedDefaultValue = useMemo(() => mapValue(defaultValue), [defaultValueJson, hasOptions]);
  // IFF the mapped value changed, update the component. Don't otherwise.
  //   Without this, initialization can fail when options change.
  const memoizedValue = useMemo(() => mappedValue, [JSON.stringify(mappedValue)]);
  const memoizedDefaultValue = useMemo(() => mappedDefaultValue, [JSON.stringify(mappedDefaultValue)]);
  /* eslint-enable react-hooks/exhaustive-deps */
  const popperSlotSx: SxProps | undefined = alignRight
    ? ([{ direction: 'rtl' }, ...(popperSx ? [popperSx] : [])] as SxProps)
    : popperSx;
  const slotProps: NonNullable<GenericAutocompleteProps<T, M>['slotProps']> = {
    ...(alignRight ? {
      paper: { sx: { direction: 'ltr' } }
    } : {}),
    ...(popperSlotSx ? { popper: { sx: popperSlotSx } } : {}),
    ...(ListboxProps ? { listbox: ListboxProps } : {}),
  };
  const defaultMultiRenderValue: NonNullable<GenericAutocompleteProps<T, M>['renderValue']> = (
    tagValue, getItemProps, ownerState
  ) => {
    const tagValues = Array.isArray(tagValue) ? tagValue : [tagValue];

    if (renderTags) {
      return renderTags(
        tagValues.filter((v): v is AutocompleteOption<T, M> => typeof v !== "string"),
        getItemProps,
        ownerState
      );
    }

    return tagValues.map((v, index) => {
      if (typeof v === "string") return null;
      const p = getItemProps({ index });
      const isDisabled = v.disabled || p.disabled || !!v.disabledChip;

      return v.label && (
        <CabTooltip key={index} title={v.tooltipTitle ? v.tooltipTitle : ''} wrapWithSpan>
          <CabToggleChip
            label={v.label}
            icon={v.icon}
            selected
            chipColor={v.color}
            translateColor
            {...p}
            disabled={isDisabled}
          />
        </CabTooltip>
      );
    });
  };
  const selectedValueRenderer: Partial<Pick<GenericAutocompleteProps<T, M>, 'renderValue'>> = renderValue
    ? { renderValue }
    : multiple ? { renderValue: defaultMultiRenderValue } : {};

  return <AutocompleteStyled<T, M>
    id={id}
    options={options}
    groupBy={groupBy}
    renderGroup={renderGroup}
    getOptionLabel={getOptionLabel}
    getOptionDisabled={getOptionsDisabled}
    filterOptions={filterOptions}
    onFocus={onFocus}
    onBlur={onBlur}
    onKeyDown={onKeyDown}
    noOptionsText={noOptionsText}
    multiple={multiple}
    disableCloseOnSelect={multiple ? true : false}
    disabled={disabled}
    readOnly={readOnly}
    freeSolo={freeSolo}
    inputValue={inputValue}
    value={memoizedValue}
    blurOnSelect={blurOnSelect}
    defaultValue={memoizedDefaultValue}
    onChange={(e, v) => {
      if (typeof v === 'string') return;
      if (Array.isArray(v)) {
        const ret: T[] = [];
        v.forEach(arrayVal => {
          if (typeof arrayVal !== 'string') {
            ret.push(arrayVal.value);
          }
        });
        onChange(ret);
      } else {
        onChange(v?.value != null ? v.value : null);
      }
    }}
    filterSelectedOptions={multiple}
    onInputChange={onInputChange && ((e, v) => onInputChange(v))}
    isOptionEqualToValue={(option, v) => {
      return typeof v !== "string" && option.value === v.value;
    }}
    renderInput={(p) => {
      if (renderInput) {
        return renderInput(p);
      }

      // Extract autoFocus from overrides and pass it to inputProps
      const autoFocus = overrides?.autoFocus;
      const inputSlotProps = autoFocus
        ? {
          ...p.slotProps,
          htmlInput: {
            ...p.slotProps?.htmlInput,
            autoFocus,
          },
        }
        : p.slotProps;
      return <CabInput {...p} slotProps={inputSlotProps} placeholder={placeholder} inputRef={inputRef} label={label}
        helperText={helperText} disabled={disabled} />;
    }}
    {...selectedValueRenderer}
    renderOption={renderOption || ((props, option) => {
      const key = option.key ? `${option.key}` : `${option.value}`;
      // Shouldn't pass `key` using spread (React warning)
      const {key: menuItemKey, ...menuItemProps} = props;   
      const optionItem = (
        <Box
          key={key}
          component="li"
          {...menuItemProps}
          sx={{
            alignItems: "center",
            display: "flex",
            fontSize: size === "small" ? 14 : 'inherit',
            gap: 0.5,
            minHeight: 36,
          }}
        >
          {(!optionIconPlacement || optionIconPlacement === "start") && option.icon}
          <Box component="span">{option.label}</Box>
          {optionIconPlacement === "end" && option.icon && (
            <Box component="span" sx={{ display: "flex", marginLeft: "auto" }}>
              {option.icon}
            </Box>
          )}
        </Box>
      );
      return option.disabled && option.tooltipTitle
        ? <Tooltip key={key} title={option.tooltipTitle}>{optionItem}</Tooltip>
        : optionItem;
    })}
    popupIcon={hideArrow || disabled || readOnly ? null : <CabIcon size="small" Icon={IoChevronDown} />}
    clearIcon={<CabIcon size="small" alt="Close" Icon={IoCloseOutline} />}
    slots={{ popper: narrow ? NarrowStyledPopper : StyledPopper }}
    slotProps={slotProps}
    {...overrides}
    size={size}
    sx={sx}
    className={className}
    disableClearable={!clearable}
    openOnFocus={openOnFocus}
    autoSelect={autoSelect}
    autoHighlight={autoHighlight}
    loading={loading}
    // ListboxProps={{ sx: (theme: Theme) => (theme.palette.mode === 'dark' ? { color: colors.white900 } : {}) }}
  />;
};

const StyledPopper = styled(Popper, { label: "StyledPopper" }
)<CabComponentProps<PopperProps>>(({ theme }) => ({
  '& .MuiAutocomplete-paper': {
    minWidth: 'fit-content',
  },
  '& .MuiAutocomplete-listbox': {
    ...(theme.palette.mode === 'dark' && {
      '& .MuiAutocomplete-option': {
        color: colors.white900,
      },
      backgroundColor: colors.white100,
    }),
    '::-webkit-scrollbar': {
      width: "10px",
      height: 8,
      backgroundColor: 'none',
    },
    '::-webkit-scrollbar-thumb': {
      background: colors.black500,
      borderRadius: 5,
    },
  }
}));

const NarrowStyledPopper = styled(
  StyledPopper, { label: "NarrowStyledPopper" }
)<CabComponentProps<PopperProps>>(({ theme }) => ({
  '& .MuiAutocomplete-listbox': {
    '::-webkit-scrollbar': {
      width: "6px",
      height: 8,
      backgroundColor: 'none',
    },
    '::-webkit-scrollbar-thumb': {
      background: colors.black500,
      borderRadius: 3,
    }
  },
}));

const AutocompleteStyled = styled(Autocomplete, {
  label: "AutocompleteStyled" }
)<CabComponentProps<GenericAutocompleteProps<unknown>>>(({ theme, multiple, size }) => ({
  '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
    fontSize: 14,
    alignContent: 'center',
    lineHeight: '25px',
    height: 32,
  },
  ...(size !== 'small' && {
    '& .MuiInputBase-root': {
      padding: 6,
    },
    '& .MuiInputBase-root .MuiAutocomplete-input': {
      padding: "2.5px 4px 2.5px 6px"
    },
  }),
  '& .MuiInputBase-root.Mui-disabled': {
    backgroundColor: colors.black50,
  }
})) as typeof GenericAutocomplete;
